/**
 * AlmaBetter Admin Portal - Design Tokens
 * Single source of truth for all design values
 */

// ============================================
// COLORS (Light Mode)
// ============================================

export const colors = {
  // Backgrounds
  pageBackground: '#F4F5F7',
  sidebarBackground: '#FFFFFF',
  cardBackground: '#FFFFFF',
  
  // Primary
  primary: '#4F46E5',
  primaryHover: '#4338CA',
  primaryContrast: '#FFFFFF',
  activeBackground: '#EEF2FF',
  activeText: '#4F46E5',
  
  // Text
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textDisabled: '#9CA3AF',
  
  // Headings
  headingPrimary: '#111827',
  headingSecondary: '#1F2937',
  headingTertiary: '#374151',
  
  // Borders
  borderMain: 'rgba(0,0,0,0.07)',
  borderLight: 'rgba(0,0,0,0.05)',
  
  // Sidebar
  sidebarText: '#6B7280',
  sidebarTextHover: '#374151',
  sidebarActive: '#4F46E5',
  sidebarActiveBg: '#EEF2FF',
  sidebarHoverBg: '#F7F7F9',
  
  // Card
  cardText: '#111827',
  cardHover: '#F9FAFB',
  
  // Input
  inputBackground: '#FFFFFF',
  inputBorder: '#E5E7EB',
  inputText: '#111827',
  inputFocus: '#4F46E5',
  
  // Status
  successBg: '#D1FAE5',
  successText: '#065F46',
  successRing: '#A7F3D0',
  warningBg: '#FEF3C7',
  warningText: '#92400E',
  warningRing: '#FDE68A',
  errorBg: '#FEE2E2',
  errorText: '#991B1B',
  errorRing: '#FECACA',
  neutralBg: '#F3F4F6',
  neutralText: '#4B5563',
  neutralRing: '#E5E7EB',
};

// ============================================
// DARK MODE COLORS
// ============================================

export const darkColors = {
  // Backgrounds
  pageBackground: '#0F172A',
  sidebarBackground: '#1E293B',
  cardBackground: '#1E293B',
  
  // Primary (same as light)
  primary: '#4F46E5',
  primaryHover: '#4338CA',
  primaryContrast: '#FFFFFF',
  activeBackground: 'rgba(79,70,229,0.15)',
  activeText: '#4F46E5',
  
  // Text
  textPrimary: '#F1F5F9',
  textSecondary: '#94A3B8',
  textDisabled: '#64748B',
  
  // Headings
  headingPrimary: '#F9FAFB',
  headingSecondary: '#E5E7EB',
  headingTertiary: '#D1D5DB',
  
  // Borders
  borderMain: 'rgba(255,255,255,0.1)',
  borderLight: 'rgba(255,255,255,0.05)',
  
  // Sidebar
  sidebarText: '#94A3B8',
  sidebarTextHover: '#F1F5F9',
  sidebarActive: '#4F46E5',
  sidebarActiveBg: 'rgba(79,70,229,0.15)',
  sidebarHoverBg: 'rgba(255,255,255,0.05)',
  
  // Card
  cardText: '#F1F5F9',
  cardHover: '#334155',
  
  // Input
  inputBackground: '#1E293B',
  inputBorder: 'rgba(255,255,255,0.1)',
  inputText: '#F1F5F9',
  inputFocus: '#4F46E5',
  
  // Status (inverted for dark)
  successBg: '#065F46',
  successText: '#D1FAE5',
  successRing: '#064E3B',
  warningBg: '#92400E',
  warningText: '#FEF3C7',
  warningRing: '#78350F',
  errorBg: '#991B1B',
  errorText: '#FEE2E2',
  errorRing: '#7F1D1D',
  neutralBg: '#374151',
  neutralText: '#D1D5DB',
  neutralRing: '#4B5563',
};

// ============================================
// TYPOGRAPHY
// ============================================

export const typography = {
  fontFamily: "'Gellix', 'Inter', sans-serif",
  sizes: {
    tableHeader: '11px',
    tableBody: '12.5px',
    sidebarSubtab: '12.5px',
    sidebarModuleTitle: '13px',
    button: '13px',
    sectionTitle: '15px',
    pageTitle: '22px',
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

// ============================================
// SPACING
// ============================================

export const spacing = {
  sidebar: {
    width: '252px',
    paddingX: '12px',
    moduleRow: '10px',
  },
  page: {
    padding: '32px',
    headerMarginBottom: '24px',
  },
  table: {
    cellPaddingX: '20px',
    cellPaddingY: '14px',
    headerPaddingX: '20px',
    paginationPaddingX: '20px',
    paginationPaddingY: '10px',
    paginationButtonPaddingX: '10px',
    paginationButtonPaddingY: '4px',
    paginationButtonMinSize: '28px',
    menuPaddingX: '16px',
    menuPaddingY: '10px',
    menuMinWidth: '200px',
    menuOptionHeight: '36px',
    menuIconSize: '16px',
    filterPaddingX: '10px',
    filterPaddingY: '7px',
    buttonPaddingX: '14px',
    buttonPaddingY: '6px',
    statusPillPaddingX: '9px',
    statusPillPaddingY: '2px',
    overlayPaddingX: '28px',
    overlayPaddingY: '20px',
    scrollbarSize: '6px',
    focusRingSize: '3px',
    selectedRowAccentWidth: '3px',
  },
};

// ============================================
// BORDER RADIUS
// ============================================

export const borderRadius = {
  button: '4px',
  input: '4px',
  sidebarRow: '6px',
  statusPill: '9999px',
  card: '16px',
  menu: '8px',
};

// ============================================
// SHADOWS
// ============================================

export const shadows = {
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  primaryButton: '0 2px 8px rgba(79,70,229,0.25)',
};

// ============================================
// TRANSITIONS
// ============================================

export const transitions = {
  fast: '150ms',
  normal: '180ms',
  slow: '300ms',
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

// ============================================
// TABLE / GRID DIMENSIONS
// ============================================

export const tableDimensions = {
  rowHeight: '46px',
  headerHeight: '44px',
  paginationButtonMinSize: '28px',
  scrollbarSize: '6px',
  focusRingSize: '3px',
  selectedRowAccentWidth: '3px',
  overlayBorderRadius: '10px',
};

// ============================================
// TABLE / GRID TYPOGRAPHY
// ============================================

export const tableTypography = {
  fontFamily: "'Gellix', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  fontSize: '12.5px',
  headerFontSize: '11px',
  headerFontWeight: 600,
  headerLetterSpacing: '0.05em',
  headerTextTransform: 'uppercase',
  bodyFontWeight: 400,
  paginationFontSize: '12px',
  paginationButtonFontSize: '12px',
  menuFontSize: '13px',
  filterFontSize: '13px',
  buttonFontSize: '13px',
  statusPillFontSize: '11.5px',
};

export default {
  colors,
  darkColors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  tableDimensions,
  tableTypography,
};
